import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform, useInView } from 'framer-motion';
import { ArrowRight, Mic, FileText, Calendar, Layers, Check, Coins, TrendingUp, Download, Shield, Plus, Minus, Camera, RefreshCw, Phone } from 'lucide-react';
import { integrationLogos, LOGOS, SCREENSHOTS, EMBLEMS } from '../components/AssetPlaceholder';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } }
};
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06 } }
};

// Product data
const products = [
  { name: 'Lumina', logo: LOGOS.lumina, descriptor: 'Clinical Intelligence', description: 'Captures patient conversations and EHR data to deliver complete, accurate SOAP notes automatically. Reduces documentation time by up to 70%.', link: '/lumina', screenshot: SCREENSHOTS.luminaCard },
  { name: 'RevQ', logo: LOGOS.revq, descriptor: 'Billing Intelligence', description: 'Automates the revenue cycle from claim submission to reimbursement. Fewer denials, faster cash, and AI-powered coding accuracy.', link: '/revq', screenshot: SCREENSHOTS.revqCard },
  { name: 'Echo', logo: LOGOS.echo, descriptor: 'Scheduling Intelligence', description: 'Always-on AI that reduces no-shows, handles patient communication, and keeps your schedule optimized without adding headcount.', link: '/echo', screenshot: SCREENSHOTS.echoCard },
];

// Enterprise feature tabs
const enterpriseFeatures = [
  { id: 'ambient', title: 'Ambient Documentation', subtitle: 'The note writes itself.', icon: Mic, description: 'Lumina listens to each patient conversation, structures the encounter data, and delivers a complete SOAP note — before the visit ends.', screenshot: SCREENSHOTS.luminaHighlight1, link: '/lumina' },
  { id: 'claims', title: 'AI Claims & Coding', subtitle: 'Every claim, coded and scrubbed.', icon: FileText, description: 'RevQ reads the signed SOAP note, applies ICD-10 and CPT codes, validates payer rules, and submits clean claims — automatically.', screenshot: SCREENSHOTS.revqHighlight1, link: '/revq' },
  { id: 'scheduling', title: 'Smart Scheduling', subtitle: 'Gaps filled. No-shows prevented.', icon: Calendar, description: 'Echo detects cancellations, reaches out to waitlisted patients, and fills empty slots — without staff intervention.', screenshot: SCREENSHOTS.echoHighlight1, link: '/echo' },
  { id: 'unified', title: 'Unified Platform', subtitle: 'One ecosystem for everything.', icon: Layers, description: 'Lumina, RevQ, and Echo share a single patient context. Notes flow to billing. Billing triggers scheduling. Everything syncs.', screenshot: SCREENSHOTS.luminaFullbleed, link: '/integrations' },
];

// Adoption phases
const adoptionPhases = [
  { phase: 1, name: 'Overlay Mode', subtitle: 'Zero Disruption', description: 'Start using AriaOne alongside your existing EHR. No data migration, no workflow changes.', features: ['Works alongside your existing EHR', 'No data migration required', 'Copy-paste structured outputs into any system', 'Full value from day one'], color: '#888' },
  { phase: 2, name: 'Semi-Automation', subtitle: 'Smart Assist', description: 'AriaOne starts doing the heavy lifting. Pre-filled notes, suggested codes, structured outputs.', features: ['Pre-fill SOAP notes from ambient capture', 'AI suggests CPT/ICD codes with confidence scores', 'Structured outputs ready to paste into your EHR', 'Save 2-3 hours/day on documentation'], color: '#666' },
  { phase: 3, name: 'Deep Integration', subtitle: 'Connected', description: 'AriaOne connects to your EHR via FHIR. Partial write-back, workflow automation, bi-directional sync.', features: ['FHIR-based connection to your EHR', 'Partial write-back — push notes, codes, claims', 'Workflow automation across billing & scheduling', 'Full audit trail and HIPAA compliance'], color: '#444' },
  { phase: 4, name: 'Full Migration', subtitle: 'Optional', description: "When you're ready, Lumina + revQ + Echo = your complete healthcare platform. No more juggling systems.", features: ['Lumina + revQ + Echo as your unified platform', 'Enable Healthcare native integration — full sync', 'Single source of truth for all clinical & billing data', "NOT required — only when you're ready"], color: '#111' },
];

const creditCosts = {
  lumina: [{ action: 'Per encounter (SOAP)', cost: '50–100 credits' }, { action: 'AI coding review', cost: '10–20 credits' }, { action: 'Ambient capture', cost: 'Included' }],
  revq: [{ action: 'Per claim', cost: '30–80 credits' }, { action: 'Denial appeal', cost: '20–40 credits' }, { action: 'Eligibility check', cost: '5 credits' }],
  echo: [{ action: 'Per scheduling action', cost: '20–50 credits' }, { action: 'AI phone call', cost: '15–30 credits' }, { action: 'Smart reminder', cost: '5 credits' }],
};

// ━━━ Miniature UI Components ━━━
const LuminaMiniUI = () => (
  <div className="relative w-full overflow-hidden rounded-xl" style={{ background: '#fafafa', height: 280 }} data-testid="lumina-mini-ui">
    <div className="absolute left-0 top-0 bottom-0" style={{ width: 80, background: '#f0f0f0', borderRight: '1px solid rgba(0,0,0,0.06)' }}>
      <div className="px-2 pt-3 space-y-1">
        <div className="h-1.5 rounded-full mb-2" style={{ width: '60%', background: 'rgba(0,0,0,0.06)' }} />
        {['S. Chen', 'J. Wilson', 'M. Park', 'R. Davis', 'T. Brown'].map((name, i) => (
          <div key={i} className="flex items-center gap-1.5 py-1.5 px-1.5 rounded" style={{ background: i === 0 ? 'rgba(107,92,231,0.1)' : 'transparent' }}>
            <div className="w-3.5 h-3.5 rounded-full flex-shrink-0" style={{ background: i === 0 ? '#6b5ce7' : 'rgba(0,0,0,0.06)' }} />
            <span style={{ fontSize: 8, color: i === 0 ? '#111' : 'rgba(0,0,0,0.35)', whiteSpace: 'nowrap' }}>{name}</span>
          </div>
        ))}
      </div>
    </div>
    <div className="absolute right-0 top-0 bottom-0" style={{ left: 80 }}>
      <div className="flex items-center gap-2 px-3 py-2" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        <span style={{ fontSize: 9, fontWeight: 600, color: '#111' }}>SOAP Note</span>
        <span className="px-1.5 py-0.5 rounded" style={{ fontSize: 7, background: 'rgba(34,197,94,0.1)', color: '#16a34a', animation: 'badgePulse 2s ease-in-out infinite' }}>AI-Generated</span>
      </div>
      <div className="px-3 py-2 space-y-2">
        {[
          { label: 'S', title: 'Subjective', lines: [85, 70, 55] },
          { label: 'O', title: 'Objective', lines: [90, 65, 80, 45] },
          { label: 'A', title: 'Assessment', lines: [75, 60] },
          { label: 'P', title: 'Plan', lines: [80, 70, 50] },
        ].map((section) => (
          <div key={section.label}>
            <div className="flex items-center gap-1.5 mb-1">
              <div className="w-3 h-3 rounded flex items-center justify-center" style={{ background: 'rgba(107,92,231,0.1)' }}>
                <span style={{ fontSize: 6, fontWeight: 700, color: '#6b5ce7' }}>{section.label}</span>
              </div>
              <span style={{ fontSize: 8, fontWeight: 600, color: 'rgba(0,0,0,0.55)' }}>{section.title}</span>
            </div>
            <div className="space-y-1 ml-[18px]">
              {section.lines.map((w, li) => (
                <div key={li} className="h-1 rounded-full" style={{ width: `${w}%`, background: 'rgba(0,0,0,0.07)' }} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="absolute pointer-events-none" style={{ left: 80, right: 0, top: 0, bottom: 0, overflow: 'hidden', zIndex: 1 }}>
      <div style={{ position: 'absolute', left: 0, right: 0, height: 28, background: 'linear-gradient(to bottom, transparent, rgba(107,92,231,0.06), transparent)', animation: 'miniScanDown 3s ease-in-out infinite' }} />
    </div>
    <div className="absolute left-0 right-0 bottom-0 pointer-events-none" style={{ height: '40%', background: 'linear-gradient(to bottom, transparent, #fafafa)', zIndex: 2 }} />
  </div>
);

const RevQMiniUI = () => (
  <div className="relative w-full overflow-hidden rounded-xl" style={{ background: '#fafafa', height: 280 }} data-testid="revq-mini-ui">
    <div className="flex gap-1.5 px-3 pt-3">
      {[
        { label: 'Clean Rate', value: '97.2%', color: '#16a34a' },
        { label: 'Collected', value: '$48.5K', color: '#111' },
        { label: 'Avg TAT', value: '12d', color: '#ca8a04' },
      ].map((stat, i) => (
        <div key={i} className="flex-1 rounded-lg px-2 py-1.5" style={{ background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: stat.color }}>{stat.value}</div>
          <div style={{ fontSize: 7, color: 'rgba(0,0,0,0.35)' }}>{stat.label}</div>
        </div>
      ))}
    </div>
    <div className="px-3 mt-2.5">
      <div className="flex items-center gap-2 mb-1.5">
        <span style={{ fontSize: 8, fontWeight: 600, color: 'rgba(0,0,0,0.35)', letterSpacing: '0.05em' }}>CLAIMS PIPELINE</span>
      </div>
      <div className="rounded-lg overflow-hidden" style={{ border: '1px solid rgba(0,0,0,0.06)' }}>
        <div className="flex items-center px-2 py-1" style={{ background: 'rgba(0,0,0,0.02)' }}>
          {['Claim ID', 'Patient', 'Amount', 'Status'].map(h => (
            <div key={h} className="flex-1" style={{ fontSize: 7, fontWeight: 600, color: 'rgba(0,0,0,0.35)' }}>{h}</div>
          ))}
        </div>
        {[
          { id: 'CLM-4892', patient: 'S. Chen', amount: '$1,240', status: 'Paid', color: '#16a34a' },
          { id: 'CLM-4893', patient: 'J. Wilson', amount: '$890', status: 'Pending', color: '#ca8a04' },
          { id: 'CLM-4894', patient: 'M. Park', amount: '$2,100', status: 'Submitted', color: '#2563eb' },
          { id: 'CLM-4895', patient: 'R. Davis', amount: '$560', status: 'Denied', color: '#dc2626' },
          { id: 'CLM-4896', patient: 'T. Brown', amount: '$1,780', status: 'Paid', color: '#16a34a' },
        ].map((row, i) => (
          <div key={i} className="flex items-center px-2 py-1.5" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
            <div className="flex-1" style={{ fontSize: 8, color: 'rgba(0,0,0,0.4)' }}>{row.id}</div>
            <div className="flex-1" style={{ fontSize: 8, color: 'rgba(0,0,0,0.7)' }}>{row.patient}</div>
            <div className="flex-1" style={{ fontSize: 8, fontWeight: 600, color: '#111' }}>{row.amount}</div>
            <div className="flex-1"><span className="px-1.5 py-0.5 rounded" style={{ fontSize: 7, background: `${row.color}12`, color: row.color }}>{row.status}</span></div>
          </div>
        ))}
      </div>
    </div>
    <div className="absolute pointer-events-none" style={{ left: 0, right: 0, top: '25%', bottom: 0, overflow: 'hidden', zIndex: 1 }}>
      <div style={{ position: 'absolute', left: 0, right: 0, height: 20, background: 'linear-gradient(to bottom, transparent, rgba(34,197,94,0.05), transparent)', animation: 'miniScanDown 4s ease-in-out infinite' }} />
    </div>
    <div className="absolute left-0 right-0 bottom-0 pointer-events-none" style={{ height: '40%', background: 'linear-gradient(to bottom, transparent, #fafafa)', zIndex: 2 }} />
  </div>
);

const EchoMiniUI = () => (
  <div className="relative w-full overflow-hidden rounded-xl" style={{ background: '#fafafa', height: 280 }} data-testid="echo-mini-ui">
    <div className="flex items-center px-3 pt-2.5 pb-1.5" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map((day, i) => (
        <div key={day} className="flex-1 text-center">
          <div style={{ fontSize: 7, color: 'rgba(0,0,0,0.35)' }}>{day}</div>
          <div style={{ fontSize: 9, fontWeight: i === 2 ? 700 : 500, color: i === 2 ? '#6b5ce7' : 'rgba(0,0,0,0.55)' }}>{17 + i}</div>
        </div>
      ))}
    </div>
    <div className="px-3 py-1.5">
      {['9:00', '10:00', '11:00', '12:00', '1:00', '2:00', '3:00'].map((time, ti) => {
        const grid = [[true,false,true,false,true],[false,true,false,true,false],[true,true,false,false,true],[false,false,true,true,false],[true,false,false,true,true],[false,true,true,false,false],[true,false,true,false,false]];
        return (
          <div key={time} className="flex items-center" style={{ height: 24, borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
            <div className="flex-shrink-0" style={{ width: 28, fontSize: 7, color: 'rgba(0,0,0,0.25)' }}>{time}</div>
            <div className="flex-1 flex gap-0.5">
              {Array.from({ length: 5 }, (_, di) => {
                const hasAppt = grid[ti]?.[di];
                const isNoShow = ti === 3 && di === 1;
                const isHighlight = di === 2 && hasAppt;
                const bg = isNoShow ? 'rgba(220,38,38,0.15)' : isHighlight ? 'rgba(107,92,231,0.2)' : hasAppt ? 'rgba(0,0,0,0.05)' : 'transparent';
                return <div key={di} className="flex-1 rounded" style={{ height: 16, background: bg, border: hasAppt || isNoShow ? 'none' : '1px solid rgba(0,0,0,0.03)' }} />;
              })}
            </div>
          </div>
        );
      })}
    </div>
    <div className="absolute bottom-0 left-0 right-0 px-3 pb-2.5 flex gap-2" style={{ zIndex: 3 }}>
      {[
        { num: '24', label: 'Scheduled', color: '#111' },
        { num: '8', label: 'Confirmed', color: '#16a34a' },
        { num: '2', label: 'No-show risk', color: '#dc2626' },
      ].map((s, i) => (
        <div key={i} className="flex-1 text-center py-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.9)', border: '1px solid rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: s.color }}>{s.num}</div>
          <div style={{ fontSize: 7, color: 'rgba(0,0,0,0.35)' }}>{s.label}</div>
        </div>
      ))}
    </div>
    <div className="absolute pointer-events-none" style={{ left: 28, right: 0, top: '12%', bottom: '30%', overflow: 'hidden', zIndex: 1 }}>
      <div style={{ position: 'absolute', left: 0, right: 0, height: 22, background: 'linear-gradient(to bottom, transparent, rgba(107,92,231,0.06), transparent)', animation: 'miniScanDown 3.5s ease-in-out infinite', animationDelay: '-1s' }} />
    </div>
    <div className="absolute left-0 right-0 bottom-0 pointer-events-none" style={{ height: '40%', background: 'linear-gradient(to bottom, transparent, #fafafa)', zIndex: 2 }} />
  </div>
);

// Hero tab data
const heroTabs = [
  { name: 'Lumina', logoWhite: LOGOS.lumina, logoBlack: LOGOS.luminaBlack, mp4: '/screenshots/lumina-hero-compressed.mp4', webm: '/screenshots/lumina-hero.webm' },
  { name: 'RevQ', logoWhite: LOGOS.revq, logoBlack: LOGOS.revqBlack, mp4: '/screenshots/revq-hero-compressed.mp4', webm: '/screenshots/revq-hero.webm' },
  { name: 'Echo', logoWhite: LOGOS.echo, logoBlack: LOGOS.echoBlack, mp4: '/screenshots/echo-hero-compressed.mp4', webm: '/screenshots/echo-hero.webm' },
];

// ━━━ HERO SECTION ━━━
const HeroSection = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const progressRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => { setReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches); }, []);
  useEffect(() => {
    clearInterval(timerRef.current);
    if (hovered || reducedMotion) return;
    timerRef.current = setInterval(() => setActiveTab(p => (p + 1) % heroTabs.length), 6000);
    return () => clearInterval(timerRef.current);
  }, [hovered, reducedMotion, activeTab]);
  useEffect(() => {
    if (progressRef.current) { progressRef.current.style.animation = 'none'; void progressRef.current.offsetHeight; progressRef.current.style.animation = (!hovered && !reducedMotion) ? 'progressFill 6s linear forwards' : 'none'; }
  }, [activeTab, hovered, reducedMotion]);

  const a = (delay) => reducedMotion ? {} : { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { delay, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } };

  const badges = [
    { label: 'HIPAA Compliant' },
    { label: 'SOC 2 Certified' },
    { label: 'FHIR Ready' },
  ];

  return (
    <section className="relative pt-24 md:pt-[120px]" style={{ minHeight: '100vh', background: '#ffffff', overflow: 'visible' }} data-testid="hero-section-light">
      <div className="absolute pointer-events-none" style={{ top: '8%', left: '50%', transform: 'translateX(-50%)', width: 640, height: 320, background: 'rgba(107,92,231,0.04)', filter: 'blur(90px)', zIndex: 0 }} />
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, rgba(0,0,0,0.035) 1px, transparent 1px)', backgroundSize: '28px 28px', zIndex: 0 }} />

      <div className="relative z-[5] max-w-[1100px] mx-auto px-4 text-center">
        {/* Badge pills */}
        <motion.div {...a(0)} className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-6" data-testid="hero-badges">
          {badges.map((b) => (
            <span key={b.label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium tracking-wide" style={{ border: '1px solid rgba(0,0,0,0.1)', color: 'rgba(0,0,0,0.5)', background: 'rgba(0,0,0,0.02)' }}>
              <Shield className="w-3 h-3" />
              {b.label}
            </span>
          ))}
        </motion.div>

        <motion.h1 {...a(0.06)} className="text-[44px] md:text-[56px] lg:text-[72px] leading-[1.12] tracking-[-0.03em] max-w-[860px] mx-auto pb-1" style={{ fontWeight: 500, background: 'linear-gradient(90deg, #222 0%, #666 25%, #222 50%, #666 75%, #222 100%)', backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', animation: 'textShimmer 6s ease-in-out infinite', overflow: 'visible' }} data-testid="hero-headline">Work smarter with AriaOne's intelligent apps</motion.h1>
        <motion.p {...a(0.18)} className="text-base md:text-lg max-w-[620px] mx-auto mt-5 leading-[1.6]" style={{ color: '#666' }} data-testid="hero-subheading">Lumina documents the visit. RevQ submits the claim. Echo fills the schedule. Three AI apps, one platform, zero busywork.</motion.p>

        <motion.div {...a(0.24)} className="flex flex-wrap items-center justify-center gap-3 mt-9" data-testid="hero-ctas">
          <button className="h-11 px-6 rounded-lg text-sm flex items-center gap-2" style={{ background: '#111', color: '#fff', fontWeight: 500 }} data-testid="hero-cta-primary">Explore the platform <ArrowRight className="w-4 h-4" /></button>
          <button className="h-11 px-5 rounded-lg text-sm font-medium flex items-center gap-2.5" style={{ border: '1px solid #ddd', color: '#111' }} data-testid="hero-cta-secondary"><Download className="w-4 h-4" /> Download
            <span className="flex items-center gap-1.5 ml-1 pl-2.5" style={{ borderLeft: '1px solid #ddd' }}>
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 opacity-40"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 opacity-40"><path d="M3 20.5v-17c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.24-.84-.76-.84-1.35zm13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27zm1.57-.89L20.5 12l-2.12-2.23-2.57 2.57 2.57 2.57zM6.05 2.66l10.76 6.22-2.27 2.27-8.49-8.49z"/></svg>
            </span>
          </button>
        </motion.div>

        {/* Tab switcher */}
        <motion.div {...a(0.32)} className="mt-14 mb-5 flex flex-col items-center" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} data-testid="hero-tab-switcher">
          <div className="relative inline-flex items-center gap-0.5 p-1 rounded-[24px]" role="tablist" aria-label="Product demos" style={{ background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.08)' }}>
            {heroTabs.map((tab, i) => (
              <button key={tab.name} onClick={() => setActiveTab(i)} role="tab" aria-selected={i === activeTab} aria-label={tab.name} className={`relative z-10 px-6 py-2.5 rounded-[20px] transition-all duration-150 cursor-pointer ${i !== activeTab ? 'hover:bg-black/[0.04]' : ''}`} data-testid={`hero-tab-${tab.name.toLowerCase()}`}>
                {i === activeTab && <motion.div layoutId="heroTabPillLight" className="absolute inset-0 rounded-[20px]" style={{ background: '#111', boxShadow: '0 1px 3px rgba(0,0,0,0.15)', zIndex: -1 }} transition={{ type: 'spring', stiffness: 400, damping: 30 }} />}
                <img src={i === activeTab ? tab.logoWhite : tab.logoBlack} alt={tab.name} className="h-[18px] object-contain" style={{ opacity: i === activeTab ? 1 : 0.5 }} />
              </button>
            ))}
          </div>
          <div className="mt-2.5 rounded-sm overflow-hidden" style={{ width: 220, height: 2, background: 'rgba(0,0,0,0.06)' }}>
            <div ref={progressRef} className="h-full rounded-sm" style={{ background: '#111', width: '0%' }} />
          </div>
        </motion.div>
      </div>

      {/* Browser mockup — minimal chrome, video playback */}
      <div className="relative z-[5] flex justify-center" data-testid="hero-mockup-wrapper">
        <motion.div
          initial={reducedMotion ? {} : { opacity: 0, y: 40, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          style={{ width: 'calc(100% - 40px)', maxWidth: 1400 }}
          data-testid="hero-mockup"
        >
          {/* White radial glow behind mockup */}
          <div className="absolute pointer-events-none" style={{ width: '110%', height: '60%', left: '-5%', bottom: '10%', background: 'radial-gradient(ellipse 85% 60% at 50% 70%, rgba(107,92,231,0.08) 0%, transparent 70%)', zIndex: 0 }} />
          <div className="relative" style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.1)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.12)', zIndex: 1 }}>
            <div className="relative overflow-hidden" style={{ aspectRatio: '1440/760' }}>
              {heroTabs.map((tab, i) => (
                <motion.video
                  key={tab.name}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  className="absolute inset-0 w-full h-full object-cover"
                  initial={false}
                  animate={{ opacity: i === activeTab ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                  data-testid={`hero-video-${i}`}
                >
                  <source src={tab.webm} type="video/webm" />
                  <source src={tab.mp4} type="video/mp4" />
                </motion.video>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Gradient overlay — fades to black, z-index 10, sits ON TOP of mockup to dissolve bottom into black section */}
      <div
        className="absolute left-0 right-0 bottom-0 pointer-events-none"
        style={{ height: '40%', zIndex: 2, background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.02) 20%, rgba(0,0,0,0.15) 45%, rgba(0,0,0,0.5) 70%, #000000 100%)' }}
        data-testid="hero-gradient-overlay"
      />
    </section>
  );
};

// ━━━ PRODUCTS SECTION ━━━
const ProductsSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const miniUIMap = { 'Lumina': <LuminaMiniUI />, 'RevQ': <RevQMiniUI />, 'Echo': <EchoMiniUI /> };

  return (
    <section ref={sectionRef} className="py-24 md:py-32" style={{ background: '#000000' }} data-testid="products-section-light">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer} className="text-center mb-16">
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white" data-testid="products-heading">Three apps. One ecosystem.</motion.h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {products.map((product, idx) => (
            <motion.div key={product.name} initial={{ opacity: 0, y: 60, scale: 0.95 }} animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}} transition={{ duration: 0.6, delay: idx * 0.15 }} className="rounded-2xl p-6 md:p-8 flex flex-col" style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,0.08)' }} data-testid={`product-card-${product.name.toLowerCase()}`}>
              <img src={product.logo} alt={`${product.name} — ${product.descriptor}`} className="h-8 mb-5 object-contain object-left" loading="lazy" style={{ filter: 'brightness(0)' }} />
              <p className="text-xs font-medium tracking-wide mb-3" style={{ color: 'rgba(0,0,0,0.4)' }}>{product.descriptor}</p>
              <p className="text-sm leading-relaxed mb-6 flex-grow" style={{ color: 'rgba(0,0,0,0.55)' }}>{product.description}</p>
              <div>{miniUIMap[product.name]}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ━━━ FHIR INTEGRATIONS SECTION ━━━
// Vendors positioned along the SVG orbital arcs (viewBox 0 0 1000 560, arcs centered at 500,476)
const ehrVendorsOnArc = [
  // Arc 1 (innermost, r=220, center 500,600)
  { name: 'ModMed', src: null, cx: 500, cy: 380 },
  // Arc 2 (middle, r=370)
  { name: 'eClinicalWorks', src: '/screenshots/eclinicalworks-1.svg', cx: 271, cy: 315 },
  { name: 'Allscripts', src: '/screenshots/allscripts-logo.png', cx: 500, cy: 230 },
  { name: 'NextGen', src: '/screenshots/nxgn-ba9bef15.png', cx: 729, cy: 315 },
  // Arc 3 (outermost, r=520)
  { name: 'Epic', src: '/screenshots/epic-logo.svg', cx: 140, cy: 220 },
  { name: 'Cerner', src: '/screenshots/2011.cerner.logo.png', cx: 360, cy: 100 },
  { name: 'athenahealth', src: '/screenshots/665115ea8a2c94a8a86b4399_hiresathena.png', cx: 640, cy: 100 },
  { name: 'DrChrono', src: '/screenshots/drchrono_logo_black_1400x399.4cd8bfb2149e.png', cx: 860, cy: 220 },
];

const FHIRIntegrationsSection = () => {
  const [activeTab, setActiveTab] = useState('fhir');
  const fhirRef = useRef(null);
  const { scrollYProgress: fhirScroll } = useScroll({ target: fhirRef, offset: ["start end", "end start"] });
  const orbitalY = useTransform(fhirScroll, [0, 1], [40, -40]);

  return (
    <section ref={fhirRef} className="pb-12 md:pb-20" style={{ background: '#000000' }} data-testid="fhir-integrations-section">
      <div style={{ background: '#111111', borderRadius: 24, margin: '0 24px', border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' }}>
        {/* Top label */}
        <div className="text-center pt-8 pb-1">
          <p className="text-[11px] font-semibold tracking-[0.15em] uppercase" style={{ color: 'rgba(255,255,255,0.4)' }} data-testid="fhir-top-label">EHR INTEGRATIONS</p>
        </div>
        {/* Tab switcher */}
        <div className="flex items-center justify-center gap-2 pt-2 pb-2" role="tablist" aria-label="Integration types" data-testid="fhir-tab-switcher">
          {[{ key: 'fhir', label: 'FHIR' }, { key: 'nonfhir', label: 'Non-FHIR' }].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              role="tab"
              aria-selected={activeTab === tab.key}
              aria-label={tab.label}
              className="px-5 py-2 rounded-full text-[13px] font-medium transition-all duration-200"
              style={{
                background: activeTab === tab.key ? 'rgba(255,255,255,0.1)' : 'transparent',
                color: activeTab === tab.key ? '#fff' : 'rgba(255,255,255,0.4)',
                border: `1px solid ${activeTab === tab.key ? 'rgba(255,255,255,0.15)' : 'transparent'}`,
              }}
              data-testid={`tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'fhir' ? (
          <div className="relative min-h-[400px] md:min-h-[640px] overflow-hidden" style={{ background: '#111111' }} data-testid="fhir-content">
            {/* Star particles */}
            <div className="absolute inset-0" style={{ zIndex: 0 }}>
              {Array.from({ length: 50 }, (_, i) => (
                <div key={i} className="absolute rounded-full" style={{ width: 1 + (i % 2), height: 1 + (i % 2), left: `${((i * 37 + 13) % 97)}%`, top: `${((i * 53 + 7) % 93)}%`, background: '#fff', opacity: 0.12 + (i % 4) * 0.05 }} />
              ))}
            </div>

            {/* SVG Orbital Arc System — parallax wrapper */}
            <motion.div className="absolute inset-0" style={{ y: orbitalY, zIndex: 2 }}>
            <svg className="w-full h-full" viewBox="0 0 1000 600" preserveAspectRatio="xMidYMid meet">
              <defs>
                <filter id="toWhite">
                  <feColorMatrix type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.85 0"/>
                </filter>
              </defs>

              {/* 3 concentric arcs centered at bottom */}
              <path d="M 280 600 A 220 220 0 0 1 720 600" stroke="rgba(255,255,255,0.14)" strokeWidth="1" fill="none" />
              <path d="M 130 600 A 370 370 0 0 1 870 600" stroke="rgba(255,255,255,0.10)" strokeWidth="1" fill="none" />
              <path d="M -20 600 A 520 520 0 0 1 1020 600" stroke="rgba(255,255,255,0.06)" strokeWidth="1" fill="none" />

              {/* Animated orbital dots */}
              <style>{`
                @keyframes orbitArc1 { from { transform: rotate(-180deg); } to { transform: rotate(0deg); } }
                .od1 { transform-origin: 500px 600px; animation: orbitArc1 4s linear infinite; }
                .od2 { transform-origin: 500px 600px; animation: orbitArc1 6s linear infinite; animation-delay: -2s; }
                .od3 { transform-origin: 500px 600px; animation: orbitArc1 8s linear infinite; animation-delay: -5s; }
              `}</style>
              <g className="od1"><circle cx="500" cy="380" r="2.5" fill="#7c6ef0" opacity="0.8" /></g>
              <g className="od2"><circle cx="500" cy="230" r="2.5" fill="#7c6ef0" opacity="0.8" /></g>
              <g className="od3"><circle cx="500" cy="80" r="2.5" fill="#7c6ef0" opacity="0.8" /></g>

              {/* Vendor badges on arcs */}
              {ehrVendorsOnArc.map((vendor) => (
                <g key={vendor.name} data-testid={`vendor-badge-${vendor.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <circle cx={vendor.cx} cy={vendor.cy} r="32" fill="#1a1a1a" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
                  {vendor.src ? (
                    <image href={vendor.src} x={vendor.cx - 16} y={vendor.cy - 16} width="32" height="32" filter="url(#toWhite)" preserveAspectRatio="xMidYMid meet" />
                  ) : (
                    <text x={vendor.cx} y={vendor.cy + 4} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="12" fontWeight="600">{vendor.name}</text>
                  )}
                </g>
              ))}
            </svg>
            </motion.div>

            {/* Bottom fade gradient */}
            <div className="absolute bottom-0 left-0 right-0" style={{ height: 200, background: 'linear-gradient(to bottom, transparent, #111111)', zIndex: 3 }} />

            {/* Text at bottom */}
            <div className="absolute bottom-0 left-0 right-0 text-center px-6 pb-12" style={{ zIndex: 5 }}>
              <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-2xl md:text-[36px] font-bold tracking-[-0.025em] leading-[1.1] mb-3 text-white" data-testid="fhir-headline">Connect to your EHR in minutes.</motion.h2>
              <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="text-[15px] leading-[1.6] mb-7 mx-auto" style={{ color: 'rgba(255,255,255,0.55)', maxWidth: 400 }} data-testid="fhir-subhead">Full bi-directional sync with your EHR, PM, and billing systems via FHIR. Zero configuration.</motion.p>
              <motion.button initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="h-[46px] px-7 rounded-full text-[14px] font-semibold cursor-pointer transition-colors duration-150 hover:bg-white/90" style={{ background: '#ffffff', color: '#0a0a0a', border: 'none' }} data-testid="fhir-cta">Explore all integrations &rarr;</motion.button>
            </div>
          </div>
        ) : (
          <div className="relative min-h-[400px] md:min-h-[560px] overflow-hidden" style={{ background: '#111111' }} data-testid="nonfhir-content">
            <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.02) 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
            <div className="relative z-10 flex flex-col items-center justify-center py-16 px-6" style={{ minHeight: 560 }}>
              <span className="inline-block mb-5 px-4 py-1.5 rounded-full text-[11px] font-medium" style={{ border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.04)' }}>NON-FHIR INTEGRATIONS</span>
              <h2 className="text-2xl md:text-[36px] font-bold tracking-[-0.025em] leading-[1.1] mb-3 text-white text-center">Legacy systems? Covered.</h2>
              <p className="text-[15px] leading-[1.6] mb-10 text-center mx-auto" style={{ color: 'rgba(255,255,255,0.55)', maxWidth: 380 }}>Lumina Lens scans incoming documents — faxes, paper forms, images — and extracts structured data automatically.</p>

              {/* Lens Bridge Animation */}
              <div className="w-full max-w-[700px] mx-auto flex flex-col md:flex-row items-center gap-4 md:gap-6">
                {/* Left: Document thumbnails */}
                <div className="flex-1 space-y-2">
                  {['Fax: Lab Results', 'Paper Superbill', 'Insurance Card Photo', 'Scanned Referral'].map((doc, i) => (
                    <motion.div key={doc} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 + i * 0.1 }} className="flex items-center gap-3 px-3 py-2.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <FileText className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'rgba(255,255,255,0.3)' }} />
                      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{doc}</span>
                    </motion.div>
                  ))}
                </div>

                {/* Center: Scanning lens beam */}
                <div className="flex flex-col items-center gap-2 flex-shrink-0">
                  <div className="relative w-12 h-32 rounded-lg overflow-hidden" style={{ background: 'rgba(107,92,231,0.05)', border: '1px solid rgba(107,92,231,0.15)' }}>
                    <div className="absolute left-0 right-0 h-6" style={{ background: 'linear-gradient(to bottom, transparent, rgba(107,92,231,0.3), transparent)', animation: 'miniScanDown 2s ease-in-out infinite' }} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Camera className="w-5 h-5" style={{ color: '#7c6ef0', opacity: 0.6 }} />
                    </div>
                  </div>
                  <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.1em' }}>LENS</span>
                </div>

                {/* Right: Extracted data */}
                <div className="flex-1 space-y-2">
                  {[
                    { label: 'Patient', value: 'Sarah Chen', color: '#fff' },
                    { label: 'Test', value: 'CBC Panel', color: '#fff' },
                    { label: 'Status', value: 'Critical', color: '#ef4444' },
                    { label: 'Filed to', value: 'EHR Record', color: '#22c55e' },
                  ].map((field, i) => (
                    <motion.div key={field.label} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 + i * 0.1 }} className="flex justify-between items-center px-3 py-2.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>{field.label}</span>
                      <span style={{ fontSize: 12, fontWeight: 500, color: field.color }}>{field.value}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Protocol tags */}
              <div className="flex flex-wrap justify-center gap-2 mt-10">
                {['HL7v2', 'CSV Import', 'Direct DB', 'REST API', 'SFTP'].map(tag => (
                  <span key={tag} className="px-3 py-1.5 rounded-full text-[12px]" style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.03)' }}>{tag}</span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// ━━━ LENS BRIDGE SECTION (Light) ━━━
const docTypes = [
  { type: 'Lab Report', fields: [{ label: 'Patient', value: 'Sarah Chen', color: '#111' }, { label: 'Test', value: 'CBC Panel', color: '#111' }, { label: 'Status', value: 'Critical', color: '#dc2626' }] },
  { type: 'Prescription', fields: [{ label: 'Drug', value: 'Metformin 500mg', color: '#111' }, { label: 'Dosage', value: 'BID', color: '#111' }, { label: 'Refills', value: '3 remaining', color: '#16a34a' }] },
  { type: 'Insurance EOB', fields: [{ label: 'Claim', value: '#CLM-4892', color: '#111' }, { label: 'Allowed', value: '$1,240.00', color: '#111' }, { label: 'Patient Resp.', value: '$186.00', color: '#eab308' }] },
  { type: 'Referral', fields: [{ label: 'From', value: 'Dr. Martinez', color: '#111' }, { label: 'To', value: 'Cardiology', color: '#111' }, { label: 'Priority', value: 'Urgent', color: '#dc2626' }] },
  { type: 'Prior Auth', fields: [{ label: 'Procedure', value: 'MRI Lumbar', color: '#111' }, { label: 'Payer', value: 'Blue Cross', color: '#111' }, { label: 'Status', value: 'Approved', color: '#16a34a' }] },
  { type: 'Discharge Summary', fields: [{ label: 'Patient', value: 'James Wilson', color: '#111' }, { label: 'LOS', value: '3 days', color: '#111' }, { label: 'Follow-up', value: '7 days', color: '#eab308' }] },
  { type: 'Pathology', fields: [{ label: 'Specimen', value: 'Biopsy #7291', color: '#111' }, { label: 'Result', value: 'Benign', color: '#16a34a' }, { label: 'Reviewed', value: 'Dr. Park', color: '#111' }] },
];

const LensBridgeSectionLight = () => {
  const [activeDoc, setActiveDoc] = useState(0);
  useEffect(() => { const t = setInterval(() => setActiveDoc(p => (p + 1) % docTypes.length), 3000); return () => clearInterval(t); }, []);

  return (
    <section className="py-24 md:py-32" style={{ background: '#ffffff' }} data-testid="lens-bridge-light">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-5" style={{ border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(0,0,0,0.02)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><rect x="2" y="4" width="20" height="16" rx="3" /><circle cx="12" cy="12" r="4" /></svg>
            <span className="text-xs font-medium" style={{ color: '#888' }}>Lumina Lens</span>
          </div>
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-[44px] font-bold tracking-tight leading-tight" style={{ color: '#111' }}>Every document, structured.</motion.h2>
          <motion.p variants={fadeInUp} className="text-sm md:text-base max-w-lg mx-auto mt-4 font-normal" style={{ color: '#666' }}>Lens scans incoming documents — labs, referrals, insurance EOBs — and extracts structured data. It's OCR + clinical NLP + auto-filing.</motion.p>
        </motion.div>

        <div className="flex gap-10 items-start max-w-[900px] mx-auto">
          {/* Left: Document stack */}
          <div className="w-[45%] space-y-2">
            {docTypes.map((doc, i) => (
              <motion.div key={i} className="flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all duration-200" style={{ background: i === activeDoc ? 'rgba(0,0,0,0.03)' : 'transparent', border: `1px solid ${i === activeDoc ? 'rgba(0,0,0,0.08)' : 'transparent'}` }} onClick={() => setActiveDoc(i)}>
                <svg viewBox="0 0 16 16" fill={i === activeDoc ? '#111' : 'rgba(0,0,0,0.15)'} width="11" height="11"><rect width="16" height="16" rx="3" /></svg>
                <span className="text-sm" style={{ color: i === activeDoc ? '#111' : '#999' }}>{doc.type}</span>
                {i === activeDoc && <div className="ml-auto w-1.5 h-1.5 rounded-full" style={{ background: '#111' }} />}
              </motion.div>
            ))}
          </div>

          {/* Right: Extracted data preview */}
          <div className="w-[55%]">
            <AnimatePresence mode="wait">
              <motion.div key={activeDoc} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }} className="rounded-xl p-6" style={{ background: '#fafafa', border: '1px solid #eee' }}>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full" style={{ background: '#6b5ce7' }} />
                  <span className="text-xs font-medium" style={{ color: '#888' }}>Structured Output</span>
                </div>
                <h3 className="text-base font-bold mb-4" style={{ color: '#111' }}>{docTypes[activeDoc].type}</h3>
                <div className="space-y-3">
                  {docTypes[activeDoc].fields.map((field, fi) => (
                    <div key={fi} className="flex justify-between items-center py-2 px-3 rounded-lg" style={{ background: 'rgba(0,0,0,0.02)' }}>
                      <span className="text-xs" style={{ color: '#999' }}>{field.label}</span>
                      <span className="text-sm font-medium" style={{ color: field.color }}>{field.value}</span>
                    </div>
                  ))}
                </div>
                {/* Scan line animation */}
                <div className="relative mt-4 h-px overflow-hidden" style={{ background: 'rgba(0,0,0,0.04)' }}>
                  <motion.div className="absolute h-full w-1/3 rounded-full" style={{ background: 'linear-gradient(90deg, transparent, rgba(107,92,231,0.4), transparent)' }} animate={{ x: ['-100%', '400%'] }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }} />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

// ━━━ ENTERPRISE FEATURES SECTION ━━━
const EnterpriseFeaturesSection = () => {
  const [activeTab, setActiveTab] = useState('ambient');
  const activeFeature = enterpriseFeatures.find(f => f.id === activeTab);

  return (
    <section className="py-24 md:py-32" style={{ background: '#fafafa', borderTop: '1px solid #eee' }} data-testid="enterprise-features-light">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-14">
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight" style={{ color: '#111' }}>Packed with rich and easy to use features</motion.h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="flex flex-wrap justify-center gap-3 mb-14">
          {enterpriseFeatures.map((feature) => (
            <button key={feature.id} onClick={() => setActiveTab(feature.id)} className="px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2" style={{ background: activeTab === feature.id ? '#111' : 'transparent', color: activeTab === feature.id ? '#fff' : '#666', border: `1px solid ${activeTab === feature.id ? '#111' : '#ddd'}` }}>
              <feature.icon className="w-4 h-4" /> {feature.title}
            </button>
          ))}
        </motion.div>
        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }} className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center">
            <div className="space-y-6">
              <div>
                <p className="text-[#888] text-xs font-medium tracking-widest uppercase mb-3">{activeFeature?.title}</p>
                <h3 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight" style={{ color: '#111' }}>{activeFeature?.subtitle}</h3>
              </div>
              <p className="text-base md:text-lg leading-relaxed max-w-lg font-normal" style={{ color: '#666' }}>{activeFeature?.description}</p>
              <Link to={activeFeature?.link || '/'} className="px-6 h-11 rounded-lg text-sm font-medium inline-flex items-center gap-2" style={{ background: '#111', color: '#fff' }}>Explore {activeFeature?.title} <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #eee', boxShadow: '0 8px 30px rgba(0,0,0,0.06)' }}>
              <div className="flex items-center gap-3 h-10 px-4" style={{ background: '#f0f0f0', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full" style={{ background: '#ff5f56' }} /><div className="w-3 h-3 rounded-full" style={{ background: '#ffbd2e' }} /><div className="w-3 h-3 rounded-full" style={{ background: '#27c93f' }} /></div>
                <div className="flex-1 h-6 rounded-md flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.03)' }}><span className="text-[12px]" style={{ color: '#999' }}>app.ariaone.ai</span></div>
              </div>
              <img src={activeFeature?.screenshot} alt={activeFeature?.title} className="w-full object-cover object-top" style={{ height: 420 }} />
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

// ━━━ INTEGRATIONS SECTION ━━━
const integrationIcons = [
  { name: 'Epic', src: '/screenshots/epic-logo.svg', cx: 300, cy: 22, shape: 'square' },
  { name: 'Cerner', src: '/screenshots/2011.cerner.logo.png', cx: 530, cy: 80, shape: 'rect' },
  { name: 'athena', src: '/screenshots/665115ea8a2c94a8a86b4399_hiresathena.png', cx: 580, cy: 220, shape: 'rect' },
  { name: 'DrChrono', src: '/screenshots/drchrono_logo_black_1400x399.4cd8bfb2149e.png', cx: 530, cy: 360, shape: 'rect' },
  { name: 'eCW', src: '/screenshots/eclinicalworks-1.svg', cx: 300, cy: 418, shape: 'rect' },
  { name: 'Allscripts', src: '/screenshots/allscripts-logo.png', cx: 70, cy: 360, shape: 'rect' },
  { name: 'NextGen', src: '/screenshots/nxgn-ba9bef15.png', cx: 20, cy: 220, shape: 'square' },
  { name: 'Meditech', src: null, cx: 70, cy: 80, shape: 'square', text: 'MT' },
];
const CENTER_X = 300, CENTER_Y = 220;

const IntegrationsSection = () => (
  <section className="py-24 md:py-32 overflow-hidden" style={{ background: '#ffffff', borderTop: '1px solid #eee' }} data-testid="integrations-section-light">
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-12">
        <motion.p variants={fadeInUp} className="text-[#888] text-xs font-medium tracking-widest uppercase mb-4">Seamless Connectivity</motion.p>
        <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-[44px] tracking-tight leading-tight" style={{ color: '#111' }}>Integrates with your entire stack</motion.h2>
        <motion.p variants={fadeInUp} className="text-sm md:text-base max-w-sm mx-auto mt-4 font-normal" style={{ color: '#888' }}>AriaOne connects to your existing EHR, PM, and billing systems via FHIR.</motion.p>
      </motion.div>

      <div className="relative w-full" style={{ maxWidth: '720px', margin: '0 auto' }}>
        <svg viewBox="0 0 600 440" className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id="lineGradLight" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor="rgba(107,92,231,0.2)" /><stop offset="50%" stopColor="rgba(107,92,231,0.08)" /><stop offset="100%" stopColor="rgba(107,92,231,0.2)" /></linearGradient>
            <radialGradient id="dotGlowLight"><stop offset="0%" stopColor="rgba(107,92,231,1)" /><stop offset="40%" stopColor="rgba(107,92,231,0.5)" /><stop offset="100%" stopColor="rgba(107,92,231,0)" /></radialGradient>
            <filter id="glowFilterLight"><feGaussianBlur stdDeviation="3" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          </defs>
          {integrationIcons.map((icon, i) => {
            const pathId = `line-light-${i}`;
            return (
              <g key={icon.name}>
                <line x1={icon.cx} y1={icon.cy} x2={CENTER_X} y2={CENTER_Y} stroke="url(#lineGradLight)" strokeWidth="1" />
                <path id={pathId} d={`M${icon.cx},${icon.cy} L${CENTER_X},${CENTER_Y}`} fill="none" stroke="none" />
                <circle r="4" fill="url(#dotGlowLight)" filter="url(#glowFilterLight)">
                  <animateMotion dur={`${2.5 + i * 0.4}s`} repeatCount="indefinite" keyTimes="0;1" keySplines="0.42 0 0.58 1" calcMode="spline"><mpath href={`#${pathId}`} /></animateMotion>
                </circle>
              </g>
            );
          })}
        </svg>

        <div className="absolute inset-0">
          {integrationIcons.map((icon, i) => {
            const isRect = icon.shape === 'rect';
            const w = isRect ? 72 : 56;
            const h = isRect ? 42 : 56;
            return (
              <motion.div key={icon.name} className="absolute flex items-center justify-center" style={{ left: `${(icon.cx / 600) * 100}%`, top: `${(icon.cy / 440) * 100}%`, width: w, height: h, marginLeft: -w / 2, marginTop: -h / 2, borderRadius: isRect ? 10 : 14, background: '#ffffff', border: '1px solid #eee', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }} initial={{ opacity: 0, scale: 0 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.1 + i * 0.07, type: "spring", stiffness: 200 }} whileHover={{ scale: 1.12, borderColor: 'rgba(107,92,231,0.4)' }}>
                {icon.src ? <img src={icon.src} alt={icon.name} className="max-w-[60%] max-h-[60%] object-contain" style={{ opacity: 0.55 }} /> : <span className="text-xs font-medium" style={{ color: '#999' }}>{icon.text}</span>}
              </motion.div>
            );
          })}
          <div className="absolute" style={{ left: `${(CENTER_X / 600) * 100}%`, top: `${(CENTER_Y / 440) * 100}%`, marginLeft: -32, marginTop: -32, width: 64, height: 64 }}>
            <div className="absolute inset-[-20px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(107,92,231,0.12) 0%, transparent 70%)' }} />
            <motion.div className="relative w-full h-full rounded-2xl flex items-center justify-center z-10" style={{ background: '#111', boxShadow: '0 6px 20px rgba(0,0,0,0.12)' }} initial={{ opacity: 0, scale: 0 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.5, type: "spring" }}>
              <img src="/screenshots/ariaone-emblem_white.png" alt="AriaOne" className="w-9 h-9 object-contain" />
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="mt-12 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center gap-6" style={{ background: 'rgba(107,92,231,0.04)', border: '1px solid rgba(107,92,231,0.1)' }}>
        <img src="/screenshots/enable-healthcare-emblem_wordmark_white.png" alt="Enable Healthcare" className="h-10 object-contain flex-shrink-0" style={{ filter: 'brightness(0)' }} />
        <div className="flex-1">
          <h4 className="text-base font-medium mb-1" style={{ color: '#111' }}>Native Integration with Enable Healthcare</h4>
          <p className="text-sm leading-relaxed font-normal" style={{ color: '#666' }}>AriaOne and all its apps are natively integrated with Enable Healthcare's EHR, Practice Management system, and Billing solutions.</p>
        </div>
        <Link to="/integrations" className="px-5 h-10 rounded-lg text-sm font-medium flex-shrink-0 flex items-center gap-2" style={{ background: '#111', color: '#fff' }}>Learn more <ArrowRight className="w-4 h-4" /></Link>
      </motion.div>

      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="text-center mt-10">
        <Link to="/integrations" className="px-6 h-11 rounded-full text-sm font-medium inline-flex items-center gap-2" style={{ border: '1px solid #ddd', color: '#111' }}>View all integrations <ArrowRight className="w-4 h-4" /></Link>
      </motion.div>
    </div>
  </section>
);

// ━━━ CREDITS & ADOPTION SECTION ━━━
const CreditsSection = () => {
  const [activePhase, setActivePhase] = useState(0);
  return (
    <section className="py-24 md:py-32" style={{ background: '#fafafa', borderTop: '1px solid #eee' }} data-testid="adoption-section-light">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-8">
          <motion.p variants={fadeInUp} className="text-[#888] text-xs font-medium tracking-widest uppercase mb-4">Your Adoption Journey</motion.p>
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight" style={{ color: '#111' }}>Start small. Scale when ready.</motion.h2>
          <motion.p variants={fadeInUp} className="text-base md:text-lg max-w-[600px] mx-auto mt-4 font-normal" style={{ color: '#666' }}>Move at your own pace — from zero-risk read-only mode to full platform migration.</motion.p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="mb-12">
          <div className="relative max-w-3xl mx-auto mb-8">
            <div className="h-[2px] rounded-full" style={{ background: '#e0e0e0' }} />
            <motion.div className="absolute top-0 left-0 h-[2px] rounded-full" style={{ background: 'linear-gradient(90deg, #111, rgba(0,0,0,0.4))' }} animate={{ width: `${((activePhase + 1) / 4) * 100}%` }} transition={{ duration: 0.5 }} />
            <div className="flex justify-between absolute -top-3 left-0 right-0">
              {adoptionPhases.map((phase, idx) => (
                <button key={idx} onClick={() => setActivePhase(idx)} className="relative group">
                  <motion.div animate={{ scale: activePhase === idx ? 1.3 : 1, backgroundColor: idx <= activePhase ? '#111' : '#ddd' }} whileHover={{ scale: 1.2 }} transition={{ duration: 0.2 }} className="w-6 h-6 rounded-full flex items-center justify-center cursor-pointer" style={{ border: '2px solid #fafafa' }}>
                    {idx < activePhase && <Check className="w-3 h-3 text-white" />}
                    {idx === activePhase && <motion.div className="w-2 h-2 rounded-full bg-white" layoutId="activePhaseLight" />}
                  </motion.div>
                  <span className={`absolute top-8 left-1/2 -translate-x-1/2 text-[10px] whitespace-nowrap font-medium ${idx <= activePhase ? 'text-[#111]' : 'text-[#bbb]'}`}>Phase {idx + 1}</span>
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div key={activePhase} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }} className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
            <div className="space-y-6">
              <div>
                <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="inline-flex items-center gap-2 mb-4">
                  <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ background: `${adoptionPhases[activePhase].color}10`, color: adoptionPhases[activePhase].color, border: `1px solid ${adoptionPhases[activePhase].color}20` }}>Phase {activePhase + 1}</span>
                  <span className="text-xs" style={{ color: '#bbb' }}>{adoptionPhases[activePhase].subtitle}</span>
                </motion.div>
                <motion.h3 initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }} className="text-3xl md:text-4xl font-bold tracking-tight" style={{ color: '#111' }}>{adoptionPhases[activePhase].name}</motion.h3>
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-base md:text-lg mt-3 leading-relaxed font-normal" style={{ color: '#666' }}>{adoptionPhases[activePhase].description}</motion.p>
              </div>
            </div>
            <div className="rounded-2xl p-6 md:p-8" style={{ background: '#fff', border: '1px solid #eee' }}>
              <motion.div className="space-y-4" initial="hidden" animate="visible" variants={staggerContainer}>
                {adoptionPhases[activePhase].features.map((feature, idx) => (
                  <motion.div key={feature} variants={fadeInUp} className="flex items-start gap-3 group">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${adoptionPhases[activePhase].color}10` }}>
                      <Check className="w-3 h-3" style={{ color: adoptionPhases[activePhase].color }} />
                    </div>
                    <span className="text-sm leading-relaxed" style={{ color: '#666' }}>{feature}</span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-center gap-3 mt-10">
          {adoptionPhases.map((phase, idx) => (
            <button key={idx} onClick={() => setActivePhase(idx)} className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 ${activePhase === idx ? 'bg-[#111] text-white' : 'bg-white border border-[#e0e0e0] text-[#888] hover:border-[#ccc] hover:text-[#111]'}`}>{phase.name}</button>
          ))}
        </div>

        {/* Credit model */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="mt-20">
          <motion.div variants={fadeInUp} className="text-center mb-10">
            <p className="text-[#888] text-xs font-medium tracking-widest uppercase mb-4">Flexible Usage Model</p>
            <h3 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: '#111' }}>Pay only for what you use</h3>
            <p className="text-sm max-w-lg mx-auto mt-3" style={{ color: '#666' }}>AriaOne uses a unified credit system. One credit equals one dollar. No seats, no subscriptions required to start.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[{ key: 'lumina', logo: LOGOS.lumina, costs: creditCosts.lumina }, { key: 'revq', logo: LOGOS.revq, costs: creditCosts.revq }, { key: 'echo', logo: LOGOS.echo, costs: creditCosts.echo }].map((product) => (
              <motion.div key={product.key} variants={fadeInUp} className="rounded-2xl p-6" style={{ background: '#fff', border: '1px solid #eee' }}>
                <div className="flex items-center gap-3 mb-5">
                  <img src={product.logo} alt={product.key} className="h-6 object-contain" style={{ filter: 'brightness(0)' }} />
                </div>
                <div className="space-y-3">
                  {product.costs.map((item) => (
                    <div key={item.action} className="flex justify-between text-sm">
                      <span style={{ color: '#666' }}>{item.action}</span>
                      <span className="font-medium" style={{ color: '#111' }}>{item.cost}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
          <motion.div variants={fadeInUp} className="rounded-2xl p-8 text-center mt-6" style={{ background: '#fff', border: '1px solid #eee' }}>
            <h3 className="text-xl font-bold mb-2" style={{ color: '#111' }}>High-Volume Subscription</h3>
            <p className="text-sm max-w-lg mx-auto mb-4" style={{ color: '#666' }}>For practices processing 500+ credits/month, a flat subscription unlocks automatically — saving up to 20%.</p>
            <div className="flex items-center justify-center gap-6">
              <div><span className="text-3xl font-medium" style={{ color: '#111' }}>$50-500</span><span className="text-sm ml-2" style={{ color: '#999' }}>Quick-add presets</span></div>
              <div className="h-8 w-px" style={{ background: '#eee' }} />
              <div><span className="text-sm font-medium" style={{ color: '#111' }}>Auto top-up available</span></div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ━━━ CTA BAND ━━━
const CTABandSection = () => {
  const ctaRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ctaRef, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 0.5], [0.95, 1]);
  return (
    <section ref={ctaRef} className="py-20 md:py-24 overflow-hidden" style={{ background: '#111' }}>
      <motion.div style={{ scale }} className="max-w-[1200px] mx-auto px-4 sm:px-6 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl text-white font-bold tracking-tight">Ready to transform your practice?</motion.h2>
          <motion.p variants={fadeInUp} className="text-base md:text-lg max-w-[500px] mx-auto mt-4 font-normal" style={{ color: '#aaa' }}>Start with one product or deploy the full platform. No long-term contracts required.</motion.p>
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
            <button className="px-6 h-11 rounded-lg text-sm font-medium flex items-center gap-2" style={{ background: '#fff', color: '#000' }}>Explore the platform <ArrowRight className="w-4 h-4" /></button>
            <button className="px-5 h-11 rounded-lg text-white text-sm font-medium flex items-center gap-2.5" style={{ border: '0.5px solid rgba(255,255,255,0.25)' }}><Download className="w-4 h-4" /> Download</button>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};


// ━━━ PARTICLE TEXTURE (shared) ━━━
const ParticleTexture = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{
    backgroundImage: `radial-gradient(1px 1px at 15% 25%, rgba(255,255,255,0.2) 0%, transparent 100%),
      radial-gradient(1.5px 1.5px at 70% 12%, rgba(255,255,255,0.15) 0%, transparent 100%),
      radial-gradient(1px 1px at 35% 55%, rgba(255,255,255,0.28) 0%, transparent 100%),
      radial-gradient(1.5px 1.5px at 85% 65%, rgba(255,255,255,0.18) 0%, transparent 100%),
      radial-gradient(1px 1px at 8% 75%, rgba(255,255,255,0.35) 0%, transparent 100%),
      radial-gradient(1px 1px at 50% 40%, rgba(255,255,255,0.22) 0%, transparent 100%),
      radial-gradient(1.5px 1.5px at 25% 90%, rgba(255,255,255,0.16) 0%, transparent 100%),
      radial-gradient(1px 1px at 60% 80%, rgba(255,255,255,0.2) 0%, transparent 100%)`,
    backgroundSize: '200px 200px, 300px 300px, 250px 250px, 180px 180px, 220px 220px, 160px 160px, 280px 280px, 240px 240px',
  }} />
);

// ━━━ PRICING SECTION ━━━
const creditCardData = [
  { name: 'Lumina', logo: LOGOS.lumina, category: 'CLINICAL INTELLIGENCE', items: [
    { name: 'Ambient Listening', cost: '0.5 credits/min' },
    { name: 'Dictation', cost: '0.5 credits/min' },
    { name: 'Lumina Lens', cost: '1\u20133 credits' },
  ]},
  { name: 'RevQ', logo: LOGOS.revq, category: 'BILLING INTELLIGENCE', items: [
    { name: 'AI Claim Generation', cost: '1\u20133 credits' },
    { name: 'AI Denial Appeal', cost: '2 credits' },
    { name: 'AI Coding Review', cost: '1 credit' },
  ]},
  { name: 'Echo', logo: LOGOS.echo, category: 'SCHEDULING INTELLIGENCE', items: [
    { name: 'Lens Scan', cost: '1\u20133 credits' },
    { name: 'AI Agent Task', cost: '1 credit' },
    { name: 'Voice Call', cost: '1 credit' },
  ]},
];

const PricingSection = () => {
  const [planTab, setPlanTab] = useState('payg');
  const [visits, setVisits] = useState(12);
  const [claims, setClaims] = useState(10);
  const [providers, setProviders] = useState(2);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });
  const [displayCost, setDisplayCost] = useState(0);
  const [displayRoi, setDisplayRoi] = useState(0);
  const costAnimRef = useRef(null);
  const roiAnimRef = useRef(null);
  const prevCostRef = useRef(0);
  const roiRevealed = useRef(false);

  const DAYS = 22;
  const MINS_SAVED = 15;
  const HOURLY = 85;
  const CLAIM_REV = 180;
  const DENIAL_AVG = 0.18;
  const DENIAL_NEW = 0.03;

  const monthlyCost = (visits * providers * 15 * DAYS) + (claims * providers * 3 * DAYS) + (visits * providers * 1 * DAYS);
  const annualCost = monthlyCost * 12;
  const hoursPerYear = visits * providers * (MINS_SAVED / 60) * DAYS * 12;
  const timeValue = hoursPerYear * HOURLY;
  const denialSavings = claims * providers * DAYS * 12 * CLAIM_REV * (DENIAL_AVG - DENIAL_NEW);
  const totalValue = timeValue + denialSavings;
  const roi = annualCost > 0 ? Math.round(totalValue / annualCost) : 0;

  useEffect(() => {
    if (!isInView) return;
    cancelAnimationFrame(costAnimRef.current);
    const from = prevCostRef.current;
    const to = monthlyCost;
    const dur = 800;
    let t0 = null;
    const tick = (ts) => {
      if (!t0) t0 = ts;
      const p = Math.min((ts - t0) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplayCost(Math.round(from + (to - from) * eased));
      if (p < 1) costAnimRef.current = requestAnimationFrame(tick);
      else prevCostRef.current = to;
    };
    costAnimRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(costAnimRef.current);
  }, [monthlyCost, isInView]);

  useEffect(() => {
    if (!isInView) return;
    if (roiRevealed.current) { setDisplayRoi(roi); return; }
    roiRevealed.current = true;
    cancelAnimationFrame(roiAnimRef.current);
    const dur = 1000;
    let t0 = null;
    const tick = (ts) => {
      if (!t0) t0 = ts;
      const p = Math.min((ts - t0) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 2);
      setDisplayRoi(Math.round(eased * roi));
      if (p < 1) roiAnimRef.current = requestAnimationFrame(tick);
    };
    roiAnimRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(roiAnimRef.current);
  }, [roi, isInView]);

  const entry = (delay) => ({
    initial: { opacity: 0, y: 20 },
    animate: isInView ? { opacity: 1, y: 0 } : {},
    transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <section ref={ref} className="px-3 md:px-6 py-16 md:py-24" style={{ background: '#000000' }} data-testid="pricing-section">
      <div
        className="relative overflow-hidden rounded-3xl"
        style={{ background: '#0a0a0a', padding: '48px 20px' }}
      >
        <ParticleTexture />
        <div className="relative z-10 max-w-[1100px] mx-auto px-2 md:px-10">
          {/* Header — staggered entry */}
          <div className="text-center mb-8">
            <motion.p {...entry(0.05)} className="text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color: 'rgba(255,255,255,.4)' }}>FLEXIBLE USAGE MODEL</motion.p>
            <motion.h2 {...entry(0.12)} className="text-[32px] md:text-[48px] font-extrabold tracking-[-0.03em] text-white mt-3 leading-[1.05]" data-testid="pricing-heading">Simple pricing. One credit = one dollar.</motion.h2>
            <motion.p {...entry(0.19)} className="text-[14px] md:text-[16px] mt-4 max-w-[480px] mx-auto" style={{ color: 'rgba(255,255,255,.5)' }}>One credit = one dollar. No seats. No long-term contracts. Use across all three apps. Credits never expire.</motion.p>
          </div>

          {/* Plan Toggle — staggered */}
          <motion.div {...entry(0.26)} className="flex justify-center mb-10">
            <div className="inline-flex p-1 rounded-full" role="tablist" aria-label="Pricing plans" style={{ background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.1)' }}>
              {[{ key: 'payg', label: 'Pay as you go' }, { key: 'volume', label: 'High-volume subscription' }].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setPlanTab(tab.key)}
                  role="tab"
                  aria-selected={planTab === tab.key}
                  className="px-4 md:px-6 py-2.5 rounded-full text-[13px] transition-all duration-200 cursor-pointer"
                  style={{
                    background: planTab === tab.key ? '#ffffff' : 'transparent',
                    color: planTab === tab.key ? '#0a0a0a' : 'rgba(255,255,255,.5)',
                    fontWeight: planTab === tab.key ? 600 : 400,
                  }}
                  data-testid={`pricing-tab-${tab.key}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </motion.div>

          <AnimatePresence mode="wait">
            {planTab === 'payg' ? (
              <motion.div key="payg" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} data-testid="pricing-payg-content">
                {/* Three credit cards — staggered */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
                  {creditCardData.map((card, ci) => (
                    <motion.div key={card.name} className="rounded-2xl p-6 md:p-7" style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.09)' }}
                      initial={{ opacity: 0, y: 20 }}
                      animate={isInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.55, delay: 0.33 + ci * 0.1, ease: [0.22, 1, 0.36, 1] }}
                      data-testid={`credit-card-${card.name.toLowerCase()}`}
                    >
                      <img src={card.logo} alt={card.name} className="h-4 mb-1" />
                      <p className="text-[10px] uppercase tracking-[0.07em] mb-5" style={{ color: 'rgba(255,255,255,.35)' }}>{card.category}</p>
                      {card.items.map((item, ii) => (
                        <div key={ii} className="flex justify-between py-2.5" style={{ borderBottom: ii < card.items.length - 1 ? '1px solid rgba(255,255,255,.06)' : 'none' }}>
                          <span className="text-[13px]" style={{ color: 'rgba(255,255,255,.65)' }}>{item.name}</span>
                          <span className="text-[13px] font-semibold text-white" style={{ fontFamily: 'monospace' }}>{item.cost}</span>
                        </div>
                      ))}
                    </motion.div>
                  ))}
                </div>

                {/* PART C — ROI Output Panel */}
                <motion.div
                  className="relative mt-3 rounded-[14px] p-6"
                  style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', overflow: 'hidden' }}
                  data-testid="roi-panel"
                  initial={{ opacity: 0, y: 16 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.55, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
                >
                  {/* Decorative glow */}
                  <div className="absolute pointer-events-none" style={{ top: -40, right: -40, width: 200, height: 200, background: 'radial-gradient(circle, rgba(124,110,240,.15) 0%, transparent 70%)', zIndex: 0 }} />

                  <div className="relative" style={{ zIndex: 1 }}>
                    {/* ROW 1 — Headline + ROI Multiple */}
                    <div className="flex gap-4 mb-5" style={{ justifyContent: 'space-between' }}>
                      <div style={{ flex: 1 }}>
                        <p className="text-[13px] font-semibold leading-[1.5]" style={{ color: 'rgba(255,255,255,.9)' }} data-testid="roi-sentence">
                          At <span style={{ color: '#7c6ef0', fontWeight: 700 }}>{visits * providers} visits/day across {providers} provider{providers > 1 ? 's' : ''}</span>, AriaOne saves your team approximately <span style={{ color: 'rgba(255,255,255,.9)' }}>{Math.round(hoursPerYear).toLocaleString()} hours</span> of documentation per year &mdash; worth <span style={{ color: '#7c6ef0', fontWeight: 700 }}>${Math.round(timeValue).toLocaleString()}</span> in recovered physician time.
                        </p>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <p className="text-[42px] font-extrabold text-white tabular-nums tracking-[-0.04em] leading-none" data-testid="roi-multiple">{displayRoi}&times;</p>
                        <p className="text-[10px] mt-1" style={{ color: 'rgba(255,255,255,.4)' }}>return on cost</p>
                      </div>
                    </div>

                    {/* ROW 2 — Three Breakdown Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
                      <div className="rounded-[9px] px-3.5 py-3" style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.07)' }} data-testid="roi-card-time">
                        <div className="flex items-center gap-1.5 mb-2">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                          <span className="text-[9px] uppercase" style={{ color: 'rgba(255,255,255,.4)' }}>TIME SAVED</span>
                        </div>
                        <p className="text-[18px] font-bold" style={{ color: '#22c55e' }} data-testid="roi-hours">{Math.round(hoursPerYear).toLocaleString()} hrs/yr</p>
                        <p className="text-[10px] leading-[1.4] mt-1" style={{ color: 'rgba(255,255,255,.4)' }}>Documentation time recovered across all providers</p>
                      </div>
                      <div className="rounded-[9px] px-3.5 py-3" style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.07)' }} data-testid="roi-card-revenue">
                        <div className="flex items-center gap-1.5 mb-2">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
                          <span className="text-[9px] uppercase" style={{ color: 'rgba(255,255,255,.4)' }}>REVENUE RECOVERED</span>
                        </div>
                        <p className="text-[18px] font-bold" style={{ color: '#22c55e' }} data-testid="roi-revenue">${Math.round(timeValue).toLocaleString()}</p>
                        <p className="text-[10px] leading-[1.4] mt-1" style={{ color: 'rgba(255,255,255,.4)' }}>Physician time value at $85/hr industry average</p>
                      </div>
                      <div className="rounded-[9px] px-3.5 py-3" style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.07)' }} data-testid="roi-card-denial">
                        <div className="flex items-center gap-1.5 mb-2">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
                          <span className="text-[9px] uppercase" style={{ color: 'rgba(255,255,255,.4)' }}>DENIAL REDUCTION</span>
                        </div>
                        <p className="text-[18px] font-bold" style={{ color: '#22c55e' }} data-testid="roi-denial">${Math.round(denialSavings).toLocaleString()}</p>
                        <p className="text-[10px] leading-[1.4] mt-1" style={{ color: 'rgba(255,255,255,.4)' }}>Est. savings from 97%+ first-pass claim rate</p>
                      </div>
                    </div>

                    {/* ROW 3 — Comparison Bar */}
                    <div className="mb-3.5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px]" style={{ color: 'rgba(255,255,255,.35)' }}>AriaOne cost</span>
                        <span className="text-[10px]" style={{ color: 'rgba(255,255,255,.35)' }} data-testid="roi-bar-label">${Math.round(monthlyCost).toLocaleString()}/mo &middot; vs ${Math.round(totalValue / 12).toLocaleString()}/mo recovered</span>
                      </div>
                      <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,.08)', overflow: 'hidden' }}>
                        <div
                          style={{
                            height: 4,
                            borderRadius: 2,
                            background: 'linear-gradient(90deg, #7c6ef0, #22c55e)',
                            width: `${Math.min(96, Math.max(20, totalValue > 0 ? (1 - (annualCost / totalValue)) * 100 : 20))}%`,
                            transition: 'width 0.8s cubic-bezier(0.22, 1, 0.36, 1)',
                          }}
                          data-testid="roi-bar-fill"
                        />
                      </div>
                    </div>

                    {/* ROW 4 — Footnote */}
                    <div style={{ borderTop: '1px solid rgba(255,255,255,.06)', paddingTop: 12 }}>
                      <p className="text-[10px] leading-[1.5]" style={{ color: 'rgba(255,255,255,.28)' }}>
                        <span style={{ color: 'rgba(255,255,255,.5)', fontWeight: 500 }}>How we calculate this:</span> Documentation savings assume 15 min/visit saved via ambient listening across all providers &times; 22 working days. Physician time valued at $85/hr (MGMA 2025 benchmark for primary care). Denial reduction assumes improvement from industry avg 18% to 3% first-pass denial rate on RevQ claims. These are conservative estimates &mdash; many practices report higher returns within the first 90 days.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Guarantee pills — staggered */}
                <motion.div
                  className="flex flex-wrap justify-center gap-3 mt-7"
                  {...entry(0.98)}
                >
                  {['No seat limits', 'Credits never expire', 'Cancel any time', 'Same-day setup'].map(g => (
                    <span key={g} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px]" style={{ border: '1px solid rgba(255,255,255,.12)', color: 'rgba(255,255,255,.55)' }}>
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#22c55e' }} />
                      {g}
                    </span>
                  ))}
                </motion.div>
              </motion.div>
            ) : (
              <motion.div key="volume" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="flex justify-center" data-testid="pricing-volume-content">
                <div className="rounded-[20px] p-8 md:p-10 text-center max-w-[600px] w-full" style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)' }}>
                  <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.04em] px-4 py-1.5 rounded-full text-white mb-5" style={{ background: '#7c6ef0' }}>HIGH-VOLUME SUBSCRIPTION</span>
                  <p className="text-[48px] md:text-[60px] font-extrabold text-white tracking-[-0.04em]">$50&ndash;500</p>
                  <p className="text-[14px] mb-7" style={{ color: 'rgba(255,255,255,.4)' }}>Quick-add credit presets &middot; Auto top-up available</p>
                  <div className="text-left space-y-0">
                    {[
                      'For practices processing 500+ credits per month',
                      'Save up to 20% compared to pay-as-you-go',
                      'Credits never expire \u2014 no monthly reset',
                      'Auto top-up ensures uninterrupted service',
                      'Use credits across Lumina, RevQ, and Echo',
                    ].map((b, i) => (
                      <div key={i} className="flex items-center gap-3 py-3" style={{ borderBottom: i < 4 ? '1px solid rgba(255,255,255,.06)' : 'none' }}>
                        <div className="w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#7c6ef0' }}>
                          <Check className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-[14px]" style={{ color: 'rgba(255,255,255,.7)' }}>{b}</span>
                      </div>
                    ))}
                  </div>
                  <Link to="/contact" className="mt-6 w-full h-[50px] rounded-full text-[15px] font-bold inline-flex items-center justify-center gap-2" style={{ background: '#fff', color: '#0a0a0a' }} data-testid="pricing-volume-cta">
                    Get started with high-volume <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

// ━━━ A DAY IN YOUR PRACTICE — APPLE-STYLE SCROLL SHOWCASE ━━━
const dayMoments = [
  {
    app: 'Lumina', tag: 'Clinical Intelligence',
    color: '#22c55e',
    logo: LOGOS.luminaBlack,
    mp4: '/screenshots/lumina-hero-compressed.mp4',
    webm: '/screenshots/lumina-hero.webm',
    headline: 'Still charting at 9 PM? Now, the note writes itself. Lumina listens and delivers a complete, evidence-backed SOAP note before the patient leaves the room.',
  },
  {
    app: 'RevQ', tag: 'Billing Intelligence',
    color: '#3b82f6',
    logo: LOGOS.revqBlack,
    mp4: '/screenshots/revq-hero-compressed.mp4',
    webm: '/screenshots/revq-hero.webm',
    headline: 'Endless denials to endless clean claims. RevQ validates codes and scrubs against payer rules automatically \u2014 97% first-pass acceptance.',
  },
  {
    app: 'Echo', tag: 'Scheduling Intelligence',
    color: '#7c6ef0',
    logo: LOGOS.echoBlack,
    mp4: '/screenshots/echo-hero-compressed.mp4',
    webm: '/screenshots/echo-hero.webm',
    headline: 'Managing tedious scheduling tasks just got a whole lot easier. Echo confirms appointments, collects co-pays, and pulls from your waitlist automatically resulting in 70% fewer no-shows.',
  },
];

const DayInPracticeSection = () => {
  const outerRef = useRef(null);
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true, amount: 0.3 });

  const { scrollYProgress } = useScroll({
    target: outerRef,
    offset: ["start start", "end end"],
  });

  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    return scrollYProgress.on("change", (v) => {
      setActiveIdx(Math.min(2, Math.floor(v * 3)));
    });
  }, [scrollYProgress]);

  /* Entrance — browser slides from right, then sticks */
  const videoEnterX = useTransform(scrollYProgress, [0, 0.1], [140, 0]);

  const scrollToMoment = (idx) => {
    const outer = outerRef.current;
    if (!outer) return;
    const total = outer.offsetHeight - window.innerHeight;
    window.scrollTo({ top: outer.offsetTop + (idx / 3) * total, behavior: 'smooth' });
  };

  return (
    <>
      {/* Section Header */}
      <div ref={headerRef} className="px-6 md:px-20" style={{ background: '#ffffff', paddingTop: 100, paddingBottom: 40, textAlign: 'center' }} data-testid="day-in-practice-header">
        <motion.p initial={{ opacity: 0, y: 20 }} animate={headerInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }} style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#bbb' }}>A day in your practice</motion.p>
        <motion.h2 initial={{ opacity: 0, y: 20 }} animate={headerInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.55, delay: 0.1, ease: [0.22, 1, 0.36, 1] }} style={{ fontSize: 44, fontWeight: 800, letterSpacing: '-0.03em', color: '#0a0a0a', lineHeight: 1.1, marginTop: 12, maxWidth: 680, marginLeft: 'auto', marginRight: 'auto' }} data-testid="day-headline">This is what changes in your clinical day with AriaOne.</motion.h2>
      </div>

      {/* 350vh sticky scroll */}
      <div ref={outerRef} style={{ height: '350vh', position: 'relative', background: '#ffffff' }} data-testid="day-in-practice">
        <div style={{ position: 'sticky', top: 0, height: '100vh', overflow: 'hidden', background: '#ffffff' }}>
          <div style={{ display: 'flex', alignItems: 'center', height: '100%', maxWidth: 1440, margin: '0 auto', padding: '0 40px', gap: 48 }}>

            {/* LEFT — Text scrolls vertically */}
            <div style={{ flex: '0 0 30%', position: 'relative' }}>
              <div style={{ display: 'grid' }}>
                {dayMoments.map((m, i) => {
                  const dir = activeIdx === i ? 0 : activeIdx > i ? -60 : 60;
                  return (
                    <div key={m.app} style={{
                      gridArea: '1/1',
                      opacity: activeIdx === i ? 1 : 0,
                      transform: `translateY(${dir}px)`,
                      transition: 'opacity 0.5s cubic-bezier(0.22,1,0.36,1), transform 0.5s cubic-bezier(0.22,1,0.36,1)',
                      pointerEvents: activeIdx === i ? 'auto' : 'none',
                    }} data-testid={`day-text-${m.app.toLowerCase()}`}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
                        <img src={m.logo} alt={m.app} style={{ height: 18 }} />
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#aaa', letterSpacing: '0.04em' }}>{m.tag}</span>
                      </div>
                      <h3 style={{ fontSize: 28, letterSpacing: '-0.02em', lineHeight: 1.4, color: '#1d1d1f' }}>
                        {(() => {
                          const idx = m.headline.search(/[.?!]\s/);
                          if (idx === -1) return <span style={{ fontWeight: 600 }}>{m.headline}</span>;
                          const first = m.headline.slice(0, idx + 1);
                          const rest = m.headline.slice(idx + 1);
                          return <><span style={{ fontWeight: 600 }}>{first}</span><span style={{ fontWeight: 300 }}>{rest}</span></>;
                        })()}
                      </h3>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* RIGHT — Browser mockup, sticks after entrance, videos softly crossfade */}
            <motion.div style={{ flex: 1, position: 'relative', x: videoEnterX }}>
              <div style={{
                borderRadius: 12, overflow: 'hidden',
                border: '1px solid rgba(0,0,0,0.1)',
                boxShadow: '0 20px 50px -10px rgba(0,0,0,0.15)',
              }} data-testid="day-video-frame">
                <div style={{ aspectRatio: '1440/760', position: 'relative', overflow: 'hidden' }}>
                  {dayMoments.map((m, i) => (
                    <video key={m.app} autoPlay loop muted playsInline preload="auto"
                      style={{
                        position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
                        opacity: activeIdx === i ? 1 : 0,
                        transition: 'opacity 0.8s ease',
                      }}
                      data-testid={`day-video-${m.app.toLowerCase()}`}
                    >
                      <source src={m.webm} type="video/webm" />
                      <source src={m.mp4} type="video/mp4" />
                    </video>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Progress dots */}
          <div style={{ position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 8, zIndex: 10 }} data-testid="day-progress">
            {dayMoments.map((m, i) => (
              <div key={i} onClick={() => scrollToMoment(i)}
                style={{
                  width: activeIdx === i ? 32 : 8, height: 8, borderRadius: 4,
                  background: activeIdx === i ? m.color : 'rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease', cursor: 'pointer',
                }}
                data-testid={`day-dot-${i}`}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};


// ━━━ FAQ SECTION ━━━
const faqItems = [
  { q: 'What is AriaOne?', a: 'AriaOne is an AI platform built for healthcare practices. It includes three apps \u2014 Lumina for clinical documentation, RevQ for billing intelligence, and Echo for scheduling \u2014 all sharing one credit system and one login.' },
  { q: 'Do I need to integrate with my EHR?', a: 'No. For EHR-connected practices, AriaOne integrates via FHIR for full bi-directional sync. For practices without FHIR, Lumina Lens lets you scan documents, superbills, and patient records to get data in without any integration.' },
  { q: 'What is a credit and how does billing work?', a: 'One credit equals one dollar. You load credits and use them across any AriaOne app \u2014 no seats, no monthly subscriptions to start, and credits never expire. High-volume practices unlock a flat subscription automatically at 500+ credits per month.' },
  { q: 'How does Lumina capture clinical notes?', a: 'Lumina listens to your patient encounter in real time using ambient AI. After the visit, it generates a complete, specialty-aware SOAP note. You review it before it\u2019s pushed to your EHR via FHIR or exported manually.' },
  { q: 'What does RevQ do with denied claims?', a: 'RevQ translates denial reason codes into plain English, generates a pre-written appeal letter, and tracks resubmission deadlines automatically. You review and send \u2014 the AI does the rest.' },
  { q: 'Is AriaOne HIPAA compliant?', a: 'Yes. All AriaOne apps are HIPAA compliant. Patient data is encrypted in transit and at rest. We maintain BAAs with all relevant vendors and follow strict PHI handling protocols.' },
  { q: 'How do I get started?', a: 'Sign up, load your first credits, and try any of the three apps. Most practices are up and running the same day. A free onboarding call is included with every new account.' },
];

const FAQSection = () => {
  const [openIdx, setOpenIdx] = useState(-1);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });
  return (
    <section ref={ref} className="px-6 md:px-20 pt-24 pb-20" style={{ background: '#ffffff' }} data-testid="faq-section">
      <div className="max-w-[860px] mx-auto">
        <div className="text-center mb-14">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }} className="text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color: '#999' }}>FREQUENTLY ASKED QUESTIONS</motion.p>
          <motion.h2 initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.55, delay: 0.1, ease: [0.22, 1, 0.36, 1] }} className="text-[32px] md:text-[44px] font-extrabold tracking-[-0.025em] mt-3 leading-[1.1]" style={{ color: '#0a0a0a' }}>Most asked questions.</motion.h2>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.55, delay: 0.2, ease: [0.22, 1, 0.36, 1] }} className="text-[16px] mt-4" style={{ color: '#888' }}>Everything you need to know before getting started.</motion.p>
        </div>
        <div>
          {faqItems.map((faq, i) => (
            <div key={i} style={{ borderBottom: '1px solid #ebebeb' }}>
              <button onClick={() => setOpenIdx(openIdx === i ? -1 : i)} className="w-full flex items-center justify-between py-[22px] text-left cursor-pointer gap-4" aria-expanded={openIdx === i} data-testid={`faq-item-${i}`}>
                <span className="text-[16px] font-semibold leading-[1.4]" style={{ color: '#0a0a0a' }}>{faq.q}</span>
                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200" style={{
                  background: openIdx === i ? '#0a0a0a' : 'transparent',
                  border: `1.5px solid ${openIdx === i ? '#0a0a0a' : '#e0e0e0'}`,
                }}>
                  <Plus className="w-3.5 h-3.5 transition-transform duration-200" style={{
                    color: openIdx === i ? '#fff' : '#999',
                    transform: openIdx === i ? 'rotate(45deg)' : 'none',
                  }} />
                </div>
              </button>
              <div style={{ maxHeight: openIdx === i ? 300 : 0, overflow: 'hidden', transition: 'max-height 0.35s cubic-bezier(0.22,1,0.36,1)' }}>
                <p className="text-[15px] leading-[1.7] pb-5" style={{ color: '#666' }}>{faq.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ━━━ FINAL CTA SECTION ━━━
const FinalCTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });
  return (
    <section ref={ref} className="px-3 md:px-6" style={{ background: '#ffffff' }} data-testid="final-cta-section">
      <motion.div
        className="relative overflow-hidden text-center"
        style={{ background: '#0a0a0a', borderRadius: '24px 24px 0 0', padding: '80px 20px' }}
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      >
        <ParticleTexture />
        <div className="relative z-10 max-w-[700px] mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color: 'rgba(255,255,255,.3)' }}>GET STARTED TODAY</p>
          <h2 className="text-[36px] md:text-[60px] font-extrabold tracking-[-0.035em] text-white mt-4 leading-[1.0]">Ready to transform your practice?</h2>
          <p className="text-[15px] md:text-[17px] mt-4 max-w-[480px] mx-auto mb-10" style={{ color: 'rgba(255,255,255,.5)' }}>Start with one app or deploy all three. No contracts. No seat licenses. Most practices are live the same day.</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/contact" className="h-[52px] px-8 rounded-full text-[15px] font-bold inline-flex items-center gap-2 bg-white" style={{ color: '#0a0a0a' }} data-testid="cta-explore">Explore the platform <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/contact" className="h-[52px] px-8 rounded-full text-[15px] font-bold inline-flex items-center text-white" style={{ border: '1px solid rgba(255,255,255,.2)' }} data-testid="cta-demo">Request a demo</Link>
          </div>
          <div className="flex items-center justify-center gap-3 mt-8 opacity-40">
            <img src={LOGOS.lumina} alt="Lumina" className="h-4" />
            <div className="w-px h-4" style={{ background: 'rgba(255,255,255,.3)' }} />
            <img src={LOGOS.revq} alt="RevQ" className="h-4" />
            <div className="w-px h-4" style={{ background: 'rgba(255,255,255,.3)' }} />
            <img src={LOGOS.echo} alt="Echo" className="h-4" />
          </div>
          <p className="text-[12px] mt-2" style={{ color: 'rgba(255,255,255,.25)' }}>One login. One credit system. One platform.</p>
        </div>
      </motion.div>
    </section>
  );
};

// ━━━ FOOTER ━━━
const footerLinks = {
  Products: [
    { l: 'Lumina', p: '/lumina' },
    { l: 'RevQ', p: '/revq' },
    { l: 'Echo', p: '/echo' },
    { l: 'Integrations', p: '/integrations' },
  ],
  Company: [
    { l: 'About', p: '/about' },
    { l: 'Careers', p: '/careers' },
    { l: 'Contact', p: '/contact' },
    { l: 'Blog', p: '/blog' },
  ],
  Legal: [
    { l: 'Privacy Policy', p: '/privacy' },
    { l: 'Terms of Service', p: '/terms' },
    { l: 'HIPAA Notice', p: '/hipaa' },
    { l: 'Security', p: '/security' },
  ],
};

const FooterSection = () => (
  <footer style={{ background: '#0a0a0a' }} data-testid="footer-section">
    <div className="px-6 md:px-20 pb-10" style={{ borderTop: '1px solid rgba(255,255,255,.06)' }}>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8 py-12" style={{ borderBottom: '1px solid rgba(255,255,255,.06)' }}>
        <div>
          <p className="text-[15px] font-bold text-white mb-2">AriaOne</p>
          <p className="text-[13px] leading-[1.6] max-w-[180px]" style={{ color: 'rgba(255,255,255,.35)' }}>The AI platform built for healthcare.</p>
        </div>
        {Object.entries(footerLinks).map(([title, links]) => (
          <div key={title}>
            <p className="text-[13px] font-semibold text-white mb-4">{title}</p>
            <ul className="space-y-1">
              {links.map(l => (
                <li key={l.l}><Link to={l.p} className="text-[13px] py-1 block transition-colors duration-150" style={{ color: 'rgba(255,255,255,.4)' }}>{l.l}</Link></li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="flex flex-col md:flex-row items-center justify-between pt-5 gap-3">
        <p className="text-[12px]" style={{ color: 'rgba(255,255,255,.25)' }}>&copy; 2026 Aria One. All rights reserved.</p>
        <div className="flex items-center gap-4">
          {['HIPAA Compliant', 'SOC 2 Type II', 'FHIR Ready'].map(b => (
            <span key={b} className="flex items-center gap-1.5 text-[11px]" style={{ color: 'rgba(255,255,255,.3)' }}>
              <Check className="w-3 h-3" /> {b}
            </span>
          ))}
        </div>
      </div>
    </div>
  </footer>
);

// ━━━ PAGE ━━━
const HomePageLight = () => (
  <div style={{ background: '#ffffff' }} data-testid="home-page-light">
    <style>{`
      @keyframes miniScanDown{0%,100%{top:0%}50%{top:75%}}
      @keyframes badgePulse{0%,100%{opacity:1}50%{opacity:.5}}
      @keyframes progressFill{0%{width:0%}100%{width:100%}}
      @keyframes textShimmer{0%{background-position:200% center}100%{background-position:-200% center}}
      @keyframes growLine{from{transform:scaleY(0.3);opacity:0.3}to{transform:scaleY(1);opacity:0.8}}
    `}</style>
    <HeroSection />
    <div style={{ height: 40, background: '#000000' }} />
    <ProductsSection />
    <FHIRIntegrationsSection />
    <DayInPracticeSection />
    <PricingSection />
    <FAQSection />
    <FinalCTASection />
    <FooterSection />
  </div>
);

export default HomePageLight;
